/** Illumination model for the transmitted-light microscope viewport. */

export interface LightingSettings {
  /** Lamp dimmer, 0–1. */
  lamp: number;
  /** Iris diaphragm opening, 0–1. Smaller openings increase contrast. */
  diaphragm: number;
  /** Condenser height, 0–1. */
  condenser: number;
  /** Illuminator switch state. */
  enabled: boolean;
}

export interface LightingResult {
  illumination: number;
  contrast: number;
  /** CSS filter values for a specimen layer. */
  cssBrightness: number;
  cssContrast: number;
  isDark: boolean;
}

function assertUnitInterval(name: string, value: number): void {
  if (!Number.isFinite(value) || value < 0 || value > 1) {
    throw new RangeError(`${name} must be between 0 and 1`);
  }
}

/**
 * Estimate light transmission and contrast from the lamp, iris, and condenser.
 * Iris closure reduces illumination while increasing edge contrast; condenser
 * position scales illumination and slightly affects contrast.
 */
export function calculateLighting(settings: LightingSettings): LightingResult {
  assertUnitInterval('lamp', settings.lamp);
  assertUnitInterval('diaphragm', settings.diaphragm);
  assertUnitInterval('condenser', settings.condenser);

  const illumination = settings.enabled
    ? settings.lamp * (0.18 + 0.82 * settings.diaphragm) * (0.55 + 0.45 * settings.condenser)
    : 0;
  const contrast = 0.82 + (1 - settings.diaphragm) * 0.48 + Math.abs(settings.condenser - 0.65) * 0.12;

  return {
    illumination,
    contrast,
    cssBrightness: illumination,
    cssContrast: contrast,
    isDark: !settings.enabled || illumination < 0.025,
  };
}
